import { useState, useEffect, useCallback } from 'react';
import { fetchData, postData, updateData, deleteData } from '../api';

/**
 * Network status from the controller
 */
export interface NetworkStatus {
  mode: 'ap' | 'client' | 'disconnected';
  hostname: string;
  wifi: {
    connected: boolean;
    ssid: string | null;
    signal: number;
    ip: string | null;
  };
  ethernet: {
    connected: boolean;
    ip: string | null;
    dhcp: boolean;
  };
  ap: {
    active: boolean;
    ssid: string | null;
    clients: number;
  };
  internet: boolean;
}

/**
 * WiFi network from scan results
 */
export interface WiFiNetwork {
  ssid: string;
  signal: number;
  security: 'open' | 'wpa' | 'wpa2' | 'wpa3';
  connected: boolean;
}

/**
 * Ethernet configuration
 */
export interface EthernetSettings {
  dhcp: boolean;
  ip?: string;
  subnet?: string;
  gateway?: string;
  dns?: string[];
}

/**
 * Access Point settings
 */
export interface APSettings {
  ssid: string;
  password: string;
  channel?: number;
}

/**
 * Hook for managing network settings
 * Provides WiFi, ethernet, and AP management functionality
 */
// Helper function to retry an operation multiple times
async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: any;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (err) {
      lastError = err;
      console.log(`[withRetry] Attempt ${attempt}/${maxRetries} failed, ${attempt < maxRetries ? 'retrying...' : 'giving up'}`);
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }
  throw lastError;
}

export function useNetworkSettings() {
  const [status, setStatus] = useState<NetworkStatus | null>(null);
  const [networks, setNetworks] = useState<WiFiNetwork[]>([]);
  const [ethernetSettings, setEthernetSettings] = useState<EthernetSettings | null>(null);
  const [apSettings, setApSettings] = useState<APSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [startingAP, setStartingAP] = useState(false);
  const [stoppingAP, setStoppingAP] = useState(false);
  const [savingEthernet, setSavingEthernet] = useState(false);
  const [settingHostname, setSettingHostname] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch current network status
   */
  const fetchStatus = useCallback(async () => {
    try {
      const data: NetworkStatus = await fetchData('/network/status');
      setStatus(data);
      setError(null);
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to fetch status:', err);
      setError('Failed to fetch network status');
    }
  }, []);

  /**
   * Scan for WiFi networks
   */
  const scanWiFi = useCallback(async (): Promise<WiFiNetwork[]> => {
    setScanning(true);
    setError(null);
    try {
      const result = await fetchData('/network/wifi/scan');
      const networkList = result.networks || [];
      setNetworks(networkList);
      return networkList;
    } catch (err: any) {
      console.error('[useNetworkSettings] WiFi scan failed:', err);
      setError('WiFi scan failed');
      return [];
    } finally {
      setScanning(false);
    }
  }, []);

  /**
   * Connect to a WiFi network (with retry)
   */
  const connectWiFi = useCallback(async (ssid: string, password: string): Promise<boolean> => {
    if (connecting) return false; // Prevent double-clicks
    setConnecting(true);
    setError(null);
    try {
      const result = await withRetry(async () => {
        const response = await postData('/network/wifi/connect', { ssid, password });
        if (!response.data.success) {
          throw new Error(response.data.error || 'Failed to connect');
        }
        return response.data;
      }, 3, 2000);
      setStatus(result.status);
      return true;
    } catch (err: any) {
      console.error('[useNetworkSettings] WiFi connect failed after retries:', err);
      setError(err.message || 'Failed to connect to WiFi');
      return false;
    } finally {
      setConnecting(false);
    }
  }, [connecting]);

  /**
   * Disconnect from current WiFi (with retry)
   */
  const disconnectWiFi = useCallback(async (): Promise<boolean> => {
    if (disconnecting) return false; // Prevent double-clicks
    setDisconnecting(true);
    setError(null);
    try {
      const result = await withRetry(async () => {
        const response = await postData('/network/wifi/disconnect', {});
        if (!response.data.success) {
          throw new Error(response.data.error || 'Disconnect failed');
        }
        return response.data;
      }, 3, 1500);
      setStatus(result.status);
      return true;
    } catch (err: any) {
      console.error('[useNetworkSettings] WiFi disconnect failed after retries:', err);
      setError('Failed to disconnect from WiFi');
      return false;
    } finally {
      setDisconnecting(false);
    }
  }, [disconnecting]);

  /**
   * Forget a saved WiFi network
   */
  const forgetWiFi = useCallback(async (ssid: string): Promise<boolean> => {
    try {
      const response = await deleteData(`/network/wifi/forget/${encodeURIComponent(ssid)}`);
      return response.data.success;
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to forget network:', err);
      setError('Failed to forget network');
      return false;
    }
  }, []);

  /**
   * Get ethernet settings
   */
  const fetchEthernetSettings = useCallback(async () => {
    try {
      const settings: EthernetSettings = await fetchData('/network/ethernet');
      setEthernetSettings(settings);
      return settings;
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to fetch ethernet settings:', err);
      return null;
    }
  }, []);

  /**
   * Update ethernet settings
   */
  const setEthernet = useCallback(async (settings: EthernetSettings): Promise<boolean> => {
    setError(null);
    try {
      const response = await updateData('/network/ethernet', settings);
      const result = response.data;
      if (result.success) {
        setEthernetSettings(settings);
        setStatus(result.status);
        return true;
      } else {
        setError(result.error || 'Failed to update ethernet settings');
        return false;
      }
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to update ethernet settings:', err);
      setError(err.response?.data?.message || 'Failed to update ethernet settings');
      return false;
    }
  }, []);

  /**
   * Get AP settings
   */
  const fetchAPSettings = useCallback(async () => {
    try {
      const result = await fetchData('/network/ap/settings');
      setApSettings(result.settings);
      return result;
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to fetch AP settings:', err);
      return null;
    }
  }, []);

  /**
   * Start AP mode (with retry)
   */
  const startAP = useCallback(async (ssid?: string, password?: string): Promise<boolean> => {
    if (startingAP) return false; // Prevent double-clicks
    setStartingAP(true);
    setError(null);
    try {
      const result = await withRetry(async () => {
        const response = await postData('/network/ap/start', { ssid, password });
        if (!response.data.success) {
          throw new Error(response.data.error || 'Failed to start AP');
        }
        return response.data;
      }, 3, 2000);
      setApSettings(result.ap);
      setStatus(result.status);
      return true;
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to start AP after retries:', err);
      setError(err.message || 'Failed to start access point');
      return false;
    } finally {
      setStartingAP(false);
    }
  }, [startingAP]);

  /**
   * Stop AP mode (with retry)
   */
  const stopAP = useCallback(async (): Promise<boolean> => {
    if (stoppingAP) return false; // Prevent double-clicks
    setStoppingAP(true);
    setError(null);
    try {
      const result = await withRetry(async () => {
        const response = await postData('/network/ap/stop', {});
        if (!response.data.success) {
          throw new Error(response.data.error || 'Failed to stop AP');
        }
        return response.data;
      }, 3, 1500);
      setStatus(result.status);
      return true;
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to stop AP after retries:', err);
      setError('Failed to stop access point');
      return false;
    } finally {
      setStoppingAP(false);
    }
  }, [stoppingAP]);

  /**
   * Generate a unique AP name
   */
  const generateAPName = useCallback(async (): Promise<string> => {
    try {
      const result = await fetchData('/network/ap/generate-name');
      return result.name;
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to generate AP name:', err);
      return 'SolarController';
    }
  }, []);

  /**
   * Set hostname
   */
  const setHostname = useCallback(async (hostname: string): Promise<boolean> => {
    setError(null);
    try {
      const response = await updateData('/network/hostname', { hostname });
      const result = response.data;
      if (result.success) {
        await fetchStatus();
        return true;
      } else {
        setError(result.error || 'Failed to set hostname');
        return false;
      }
    } catch (err: any) {
      console.error('[useNetworkSettings] Failed to set hostname:', err);
      setError(err.response?.data?.message || 'Failed to set hostname');
      return false;
    }
  }, [fetchStatus]);

  /**
   * Factory reset network settings
   */
  const factoryReset = useCallback(async (): Promise<boolean> => {
    setError(null);
    try {
      const response = await postData('/network/factory-reset', { confirm: true });
      const result = response.data;
      if (result.success) {
        setApSettings(result.ap);
        setStatus(result.status);
        return true;
      } else {
        setError(result.error || 'Factory reset failed');
        return false;
      }
    } catch (err: any) {
      console.error('[useNetworkSettings] Factory reset failed:', err);
      setError(err.response?.data?.message || 'Factory reset failed');
      return false;
    }
  }, []);

  /**
   * Check internet connectivity
   */
  const checkInternet = useCallback(async (): Promise<boolean> => {
    try {
      const result = await fetchData('/network/internet');
      return result.connected;
    } catch {
      return false;
    }
  }, []);

  // Auto-refresh status every 5 seconds
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchStatus(),
        fetchEthernetSettings(),
        fetchAPSettings(),
      ]);
      setLoading(false);
    };
    init();
  }, [fetchStatus, fetchEthernetSettings, fetchAPSettings]);

  return {
    // State
    status,
    networks,
    ethernetSettings,
    apSettings,
    loading,
    scanning,
    connecting,
    disconnecting,
    startingAP,
    stoppingAP,
    savingEthernet,
    settingHostname,
    resetting,
    error,

    // Actions
    scanWiFi,
    connectWiFi,
    disconnectWiFi,
    forgetWiFi,
    setEthernet,
    startAP,
    stopAP,
    generateAPName,
    setHostname,
    factoryReset,
    checkInternet,
    refreshStatus: fetchStatus,
    clearError: () => setError(null),
  };
}
